import React, { useContext, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CompanyCodeListCityEmailsSetting,
  CompanyCodeListLocationEmailsSetting,
  CompanyCodeListNotificationEmailsSetting,
  ListCompanyCodeListItem,
  MassActionCompanyCodeListDataRequest,
  MessageResponse,
} from '@api/ringil-api.v2';
import useForm from '@hooks/useForm/useForm';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import Section from '@components/obsolete/Section/Section.tsx';
import CostCenterNotificationFields, {
  duplicateRowsError,
  emailSetsSchema,
  toRowErrors,
} from '@app/pages/customer/CompanyProfile/sidebar/CostCenterNotificationFields.tsx';
interface ComponentProps {
  items: ListCompanyCodeListItem[];
  onChanged: (updatedCount: number) => void;
}

const locationRowsToSets = (rows: CompanyCodeListLocationEmailsSetting[]): CompanyCodeListNotificationEmailsSetting[] =>
  (rows ?? []).map(r => ({ cityMatch: false, companyLocationId: r.companyLocationId, emails: r.emails ?? [] }));

const setsToLocationRows = (sets: CompanyCodeListNotificationEmailsSetting[]): CompanyCodeListLocationEmailsSetting[] =>
  (sets ?? []).map(s => ({ companyLocationId: s.companyLocationId, emails: s.emails ?? [] }));

const cityRowsToSets = (rows: CompanyCodeListCityEmailsSetting[]): CompanyCodeListNotificationEmailsSetting[] =>
  (rows ?? []).map(r => ({ cityMatch: true, cityMatcher: r.cityMatcher ?? '', emails: r.emails ?? [] }));

const setsToCityRows = (sets: CompanyCodeListNotificationEmailsSetting[]): CompanyCodeListCityEmailsSetting[] =>
  (sets ?? []).map(s => ({ cityMatcher: s.cityMatcher, emails: s.emails ?? [] }));

export const useCreateMassEditCompanyCatalogItemsSidebar = () => {
  const [visible, setVisible] = useState(false);
  const { tr } = useTranslationLgs();
  const { massActionCompanyCodeList } = useContext(ApiContext);

  const MassEditCompanyCatalogItemsSidebar: React.FC<ComponentProps> = ({ items, onChanged }) => {
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loading, setLoading] = useState(false);

    const emailsSchema = emailSetsSchema(tr('validation.valid_email', 'Value has to be valid email address.'));

    const duplicateLocationMessage = tr('EditCompanyCatalogItemSidebar.duplicateLocation', 'Each location can be used only once');
    const duplicateCityMessage = tr('EditCompanyCatalogItemSidebar.duplicateCityMatcher', 'Each city match can be used only once');

    const validationSchema = yup.object().shape({
      overwriteHtmlContent: yup
        .boolean()
        .test(
          'atLeastOneReplace',
          tr('MassEditCompanyCatalogItemsSidebar.nothingToReplace', 'Select at least one setting to replace'),
          function (value) {
            return value === true || this.parent.overwriteLocationSettings === true || this.parent.overwriteCitySettings === true;
          },
        ),
      locationSettings: yup.array().when('overwriteLocationSettings', {
        is: true,
        then: emailsSchema.test('duplicateLocation', duplicateLocationMessage, function (value: CompanyCodeListLocationEmailsSetting[]) {
          return duplicateRowsError(this, value, r => r.companyLocationId ?? '', 'companyLocationId', duplicateLocationMessage);
        }),
        otherwise: yup.array().nullable(),
      }),
      citySettings: yup.array().when('overwriteCitySettings', {
        is: true,
        then: emailsSchema.test('duplicateCityMatcher', duplicateCityMessage, function (value: CompanyCodeListCityEmailsSetting[]) {
          return duplicateRowsError(this, value, r => (r.cityMatcher ?? '').trim().toLowerCase(), 'cityMatcher', duplicateCityMessage);
        }),
        otherwise: yup.array().nullable(),
      }),
    });

    const form = useForm<MassActionCompanyCodeListDataRequest>(
      validationSchema,
      {
        overwriteHtmlContent: false,
        htmlContent: '',
        overwriteLocationSettings: false,
        locationSettings: [],
        overwriteCitySettings: false,
        citySettings: [],
      },
      values => {
        setLoading(true);
        setBeValidationErrors([]);
        const internalData: MassActionCompanyCodeListDataRequest = {
          overwriteHtmlContent: values.overwriteHtmlContent,
          htmlContent: values.overwriteHtmlContent ? values.htmlContent : undefined,
          overwriteLocationSettings: values.overwriteLocationSettings,
          locationSettings: values.overwriteLocationSettings ? values.locationSettings ?? [] : undefined,
          overwriteCitySettings: values.overwriteCitySettings,
          citySettings: values.overwriteCitySettings ? values.citySettings ?? [] : undefined,
        };
        massActionCompanyCodeList(
          { codeListIds: items.map(i => i.id), internalData },
          () => {
            setLoading(false);
            setVisible(false);
            onChanged(items.length);
          },
          null,
          {
            onValidationFailed: (messages: MessageResponse[]) => {
              setLoading(false);
              setBeValidationErrors(messages);
            },
            onEntityNotFound: () => setLoading(false),
            onConflict: () => setLoading(false),
            onError: (error, showStandardError) => {
              setLoading(false);
              showStandardError();
            },
          },
        );
      },
      false,
      false,
    );

    const atLeastOneError = typeof form.errors.overwriteHtmlContent === 'string' ? form.errors.overwriteHtmlContent : undefined;

    return (
      <RingilSidebar
        visible={visible}
        heading={tr('MassEditCompanyCatalogItemsSidebar.editCostCentres', 'Edit {{count}} cost centres', { count: items.length })}
        onBack={() => setVisible(false)}
        onContinue={() => form.validateAndSend()}
        loading={loading}
      >
        <div className={'flex flex-col gap-2'}>
          <BackendValidationComponent />
          {atLeastOneError && <small className={'p-error'}>{atLeastOneError}</small>}

          <Section
            label={tr('MassEditCompanyCatalogItemsSidebar.replaceHtmlContent', 'Replace HTML content')}
            showToggle={true}
            onVisibilityChanged={v => form.setFieldValue(form.names.overwriteHtmlContent, v)}
          >
            <CostCenterNotificationFields
              showHtmlContent
              alwaysShowHtmlContent
              htmlContent={form.values.htmlContent}
              notificationEmails={[]}
              onChangeHtmlContent={v => form.setFieldValue(form.names.htmlContent, v)}
              onChangeNotificationEmails={() => {}}
            />
          </Section>

          <Section
            label={tr('MassEditCompanyCatalogItemsSidebar.replaceLocationSettings', 'Replace location settings')}
            showToggle={true}
            onVisibilityChanged={v => form.setFieldValue(form.names.overwriteLocationSettings, v)}
          >
            <CostCenterNotificationFields
              showLocationSets
              notificationEmails={locationRowsToSets(form.values.locationSettings)}
              rowErrors={toRowErrors(form.errors.locationSettings)}
              onChangeHtmlContent={() => {}}
              onChangeNotificationEmails={v => form.setFieldValue(form.names.locationSettings, setsToLocationRows(v))}
            />
          </Section>

          <Section
            label={tr('MassEditCompanyCatalogItemsSidebar.replaceCitySettings', 'Replace city settings')}
            showToggle={true}
            onVisibilityChanged={v => form.setFieldValue(form.names.overwriteCitySettings, v)}
          >
            <CostCenterNotificationFields
              showCitySets
              notificationEmails={cityRowsToSets(form.values.citySettings)}
              rowErrors={toRowErrors(form.errors.citySettings)}
              onChangeHtmlContent={() => {}}
              onChangeNotificationEmails={v => form.setFieldValue(form.names.citySettings, setsToCityRows(v))}
            />
          </Section>
        </div>
      </RingilSidebar>
    );
  };

  return { setVisible, MassEditCompanyCatalogItemsSidebar };
};
